import { FormEvent, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { NavLink, Navigate, Route, Routes } from "react-router-dom"
import {
  KontaktFunktion,
  TextvorlageInput,
  Zertifikatsart,
  createKontaktFunktion,
  createTextvorlage,
  createZertifikatsart,
  deleteTextvorlage,
  fetchKontaktFunktionen,
  fetchTextvorlagen,
  fetchZertifikatsartenListe,
  updateTextvorlage,
} from "@/lib/api"
import { SkeletonTabelle } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { useToast } from "@/components/Toast"
import { Button, FieldError, Input, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS, Textarea } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const PFLEGE_NAV = [
  { pfad: "/pflege/zertifikatsarten", titel: "Zertifikatsarten" },
  { pfad: "/pflege/kontaktfunktionen", titel: "Kontaktfunktionen" },
  { pfad: "/pflege/textvorlagen", titel: "Textvorlagen" },
] as const

/**
 * Pflege-Bereich: kleine Lookup-CRUD-Seiten (Zertifikatsarten,
 * Kontaktfunktionen, Textvorlagen) — Empfehlung 4/5 des
 * Strukturvergleichs. Eigene Untermodul-Tabs analog UX_KONZEPT.md §1.2
 * Zeile 3.
 */
export function PflegePage() {
  return (
    // Volle Breite wie alle Listen-Seiten (Padding kommt vom Layout-<main>)
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Pflege</h1>
        <p className="text-sm text-slate-600">
          Lookup-Stammdaten: Zertifikatsarten, Kontaktfunktionen, Textvorlagen.
        </p>
      </header>

      <nav className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 pb-2 text-sm">
        {PFLEGE_NAV.map((n) => (
          <NavLink
            key={n.pfad}
            to={n.pfad}
            className={({ isActive }) =>
              `shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                isActive
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            {n.titel}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route path="zertifikatsarten" element={<ZertifikatsartenTab />} />
        <Route path="kontaktfunktionen" element={<KontaktfunktionenTab />} />
        <Route path="textvorlagen" element={<TextvorlagenTab />} />
        <Route path="*" element={<Navigate to="/pflege/zertifikatsarten" replace />} />
      </Routes>
    </div>
  )
}

// --- Zertifikatsarten (Ninox `CF`) ------------------------------------------

function ZertifikatsartenTab() {
  useEffect(() => {
    document.title = "Zertifikatsarten — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState("")
  const [dauer, setDauer] = useState<string>("")
  const [nameFehler, setNameFehler] = useState<string | null>(null)
  const [zeigeNeu, setZeigeNeu] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["zertifikatsarten"],
    queryFn: () => fetchZertifikatsartenListe(),
  })

  const anlegen = useMutation({
    mutationFn: () => createZertifikatsart(name, dauer ? Number(dauer) : null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zertifikatsarten"] })
      setName("")
      setDauer("")
      setZeigeNeu(false)
      toast.show("Zertifikatsart angelegt.", "success")
    },
    onError: () => toast.show("Zertifikatsart konnte nicht angelegt werden.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setNameFehler("Name ist erforderlich.")
      document.getElementById("za-name")?.focus()
      return
    }
    setNameFehler(null)
    anlegen.mutate()
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => setZeigeNeu(true)}>+ Neue Zertifikatsart</Button>
      </div>

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="zertifikatsart-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Zertifikatsart</SheetTitle>
            <SheetDescription>Lookup-Wert für Zertifikate anlegen.</SheetDescription>
          </SheetHeader>
          <form id="za-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="za-name" className="block text-xs font-medium text-slate-700">
                Name *
              </label>
              <Input
                id="za-name"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (nameFehler) setNameFehler(null)
                }}
                invalid={!!nameFehler}
                aria-describedby={nameFehler ? "za-name-fehler" : undefined}
              />
              <FieldError id="za-name-fehler">{nameFehler}</FieldError>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">
                Gültigkeitsdauer (Monate)
              </label>
              <Input type="number" value={dauer} onChange={(e) => setDauer(e.target.value)} />
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="za-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Zertifikatsarten konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Name</th>
              <th className={TH_CLASS}>Gültigkeitsdauer</th>
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={4} spalten={2} />
          ) : (
            <tbody>
              {(data as Zertifikatsart[] | undefined)?.map((a) => (
                <tr key={a.id} className={TR_CLASS}>
                  <td className={`${TD_CLASS} text-slate-900`}>{a.name}</td>
                  <td className={TD_CLASS}>
                    {a.gueltigkeitsdauerMonate != null ? `${a.gueltigkeitsdauerMonate} Monate` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Zertifikatsarten angelegt — ohne mindestens eine Art können keine Zertifikate erfasst werden." />
      )}
    </div>
  )
}

// --- Kontaktfunktionen (Ninox `L`) ------------------------------------------

function KontaktfunktionenTab() {
  useEffect(() => {
    document.title = "Kontaktfunktionen — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState("")
  const [nameFehler, setNameFehler] = useState<string | null>(null)
  const [zeigeNeu, setZeigeNeu] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["kontakt-funktionen"],
    queryFn: () => fetchKontaktFunktionen(),
  })

  const anlegen = useMutation({
    mutationFn: () => createKontaktFunktion(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kontakt-funktionen"] })
      setName("")
      setZeigeNeu(false)
      toast.show("Funktion angelegt.", "success")
    },
    onError: () => toast.show("Funktion konnte nicht angelegt werden.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setNameFehler("Name ist erforderlich.")
      document.getElementById("kf-name")?.focus()
      return
    }
    setNameFehler(null)
    anlegen.mutate()
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => setZeigeNeu(true)}>+ Neue Kontaktfunktion</Button>
      </div>

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="kontaktfunktion-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Kontaktfunktion</SheetTitle>
            <SheetDescription>Lookup-Wert für Kontakt-Funktionen anlegen.</SheetDescription>
          </SheetHeader>
          <form id="kf-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="kf-name" className="block text-xs font-medium text-slate-700">
                Name *
              </label>
              <Input
                id="kf-name"
                required
                placeholder="z. B. Einkauf, Geschäftsführung"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (nameFehler) setNameFehler(null)
                }}
                invalid={!!nameFehler}
                aria-describedby={nameFehler ? "kf-name-fehler" : undefined}
              />
              <FieldError id="kf-name-fehler">{nameFehler}</FieldError>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="kf-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Kontaktfunktionen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Name</th>
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={4} spalten={1} />
          ) : (
            <tbody>
              {(data as KontaktFunktion[] | undefined)?.map((f) => (
                <tr key={f.id} className={TR_CLASS}>
                  <td className={`${TD_CLASS} text-slate-900`}>{f.name}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Kontaktfunktionen angelegt." />
      )}
    </div>
  )
}

// --- Textvorlagen (Ninox `ZC`) ----------------------------------------------

const LEER_TV: TextvorlageInput = { kurztext: "", vorlagetext: "", typ: "", position: "" }

function TextvorlagenTab() {
  useEffect(() => {
    document.title = "Textvorlagen — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState<TextvorlageInput>(LEER_TV)
  const [vorlagetextFehler, setVorlagetextFehler] = useState<string | null>(null)
  const [zeigeNeu, setZeigeNeu] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["textvorlagen", "alle"],
    queryFn: () => fetchTextvorlagen(false),
  })

  const anlegen = useMutation({
    mutationFn: (payload: TextvorlageInput) => createTextvorlage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["textvorlagen"] })
      setForm(LEER_TV)
      setZeigeNeu(false)
      toast.show("Textvorlage angelegt.", "success")
    },
    onError: () => toast.show("Textvorlage konnte nicht angelegt werden.", "error"),
  })

  const deaktivieren = useMutation({
    mutationFn: (id: string) => updateTextvorlage(id, { aktiv: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["textvorlagen"] })
      toast.show("Textvorlage deaktiviert.", "success")
    },
    onError: () => toast.show("Deaktivieren fehlgeschlagen.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteTextvorlage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["textvorlagen"] })
      toast.show("Textvorlage gelöscht.", "success")
    },
    onError: () => toast.show("Löschen fehlgeschlagen.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.vorlagetext.trim()) {
      setVorlagetextFehler("Vorlagetext ist erforderlich.")
      document.getElementById("tv-text")?.focus()
      return
    }
    setVorlagetextFehler(null)
    anlegen.mutate(form)
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => setZeigeNeu(true)}>+ Neue Textvorlage</Button>
      </div>

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="textvorlage-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Textvorlage</SheetTitle>
            <SheetDescription>Textbaustein anlegen.</SheetDescription>
          </SheetHeader>
          <form id="tv-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Kurztext</label>
                <Input
                  value={form.kurztext ?? ""}
                  onChange={(e) => setForm({ ...form, kurztext: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Typ</label>
                <Input
                  value={form.typ ?? ""}
                  onChange={(e) => setForm({ ...form, typ: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="tv-text" className="block text-xs font-medium text-slate-700">
                Vorlagetext *
              </label>
              <Textarea
                id="tv-text"
                required
                rows={5}
                value={form.vorlagetext}
                onChange={(e) => {
                  setForm({ ...form, vorlagetext: e.target.value })
                  if (vorlagetextFehler) setVorlagetextFehler(null)
                }}
                invalid={!!vorlagetextFehler}
                aria-describedby={vorlagetextFehler ? "tv-text-fehler" : undefined}
              />
              <FieldError id="tv-text-fehler">{vorlagetextFehler}</FieldError>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="tv-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Textvorlagen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Kurztext</th>
              <th className={TH_CLASS}>Typ</th>
              <th className={TH_CLASS}>Status</th>
              <th className={TH_CLASS} />
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={4} spalten={4} />
          ) : (
            <tbody>
              {data?.map((v) => (
                <tr key={v.id} className={TR_CLASS}>
                  <td className={`${TD_CLASS} text-slate-900`}>{v.kurztext ?? "—"}</td>
                  <td className={TD_CLASS}>{v.typ ?? "—"}</td>
                  <td className={TD_CLASS}>{v.aktiv ? "Aktiv" : "Inaktiv"}</td>
                  <td className={`${TD_CLASS} text-right`}>
                    <div className="flex justify-end gap-2">
                      {v.aktiv && (
                        <button
                          type="button"
                          onClick={() => deaktivieren.mutate(v.id)}
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                        >
                          Deaktivieren
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => loeschen.mutate(v.id)}
                        className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Textvorlagen angelegt." />
      )}
    </div>
  )
}
